import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TaskRow } from '../components/TaskRow.jsx';
import { todayStr, getNextDueDate, inferRecurringStart, snapDeadlineToPattern } from '../lib/dates.js';

export function CalendarPage({ theme, profile, partner, tasks, onComplete, onDelete, onEdit }) {
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const today = todayStr();

  // Build month grid with recurring task expansion
  const monthData = useMemo(() => {
    const [y, m] = viewMonth.split('-').map(Number);
    const firstDay = new Date(y, m - 1, 1);
    const lastDay = new Date(y, m, 0);
    const daysInMonth = lastDay.getDate();
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;

    const monthStart = `${y}-${String(m).padStart(2,'0')}-01`;
    const monthEnd = `${y}-${String(m).padStart(2,'0')}-${String(daysInMonth).padStart(2,'0')}`;
    const tasksByDate = {};

    tasks.forEach(t => {
      // Non-recurring task: only show if it has a deadline within this month
      if (!t.repeatType || t.repeatType === 'none') {
        if (!t.deadline) return;
        const baseDate = t.deadline.split('T')[0];
        if (baseDate >= monthStart && baseDate <= monthEnd) {
          if (!tasksByDate[baseDate]) tasksByDate[baseDate] = [];
          tasksByDate[baseDate].push(t);
        }
        return;
      }

      // Recurring task: compute the starting date.
      // Either the explicit deadline OR an inferred starting point if no deadline.
      let currentDate;
      if (t.deadline) {
        currentDate = t.deadline.split('T')[0];
        // For weekday repeats, snap forward to the first valid weekday
        // (handles the case where the user changed weekdays after creation
        // and the original deadline no longer matches the new pattern).
        if (t.repeatType === 'weekday') {
          currentDate = snapDeadlineToPattern(t, currentDate);
        }
      } else {
        // Infer a starting date based on repeat type
        currentDate = inferRecurringStart(t);
        if (!currentDate) return;
      }

      // Walk forward to find all occurrences within month
      let safety = 0;
      while (currentDate <= monthEnd && safety < 800) {
        if (currentDate >= monthStart && currentDate <= monthEnd) {
          if (!tasksByDate[currentDate]) tasksByDate[currentDate] = [];
          tasksByDate[currentDate].push(t);
        }
        const next = getNextDueDate(currentDate, t.repeatType, t.interval, t.monthDay, t.weekday, t.weekdays || t.repeatWeekdays);
        if (!next || next <= currentDate) break;
        currentDate = next;
        safety++;
      }
    });

    const cells = [];
    for (let i = 0; i < startDayOfWeek; i++) cells.push({ empty: true });
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const dayTasks = tasksByDate[dateStr] || [];
      cells.push({
        date: dateStr, day: d, tasks: dayTasks,
        hasSent: dayTasks.some(t => t.list === 'sent' && t.assignedTo === profile?.id),
        hasShared: dayTasks.some(t => t.list === 'shared'),
        hasPrivate: dayTasks.some(t => t.list === 'private'),
        hasOutgoing: dayTasks.some(t => t.list === 'sent' && t.createdBy === profile?.id && t.assignedTo !== profile?.id),
      });
    }
    return cells;
  }, [viewMonth, tasks, profile]);

  // Tasks for selected date (with recurring expansion)
  const selectedTasks = useMemo(() => {
    return tasks.filter(t => {
      // Non-recurring: only show if deadline matches
      if (!t.repeatType || t.repeatType === 'none') {
        if (!t.deadline) return false;
        return t.deadline.split('T')[0] === selectedDate;
      }

      // Recurring: walk forward from the start date
      let currentDate = t.deadline ? t.deadline.split('T')[0] : inferRecurringStart(t);
      if (!currentDate) return false;
      // For weekday repeats, snap forward to first valid weekday (in case
      // the task was edited to a different weekday after creation).
      if (t.repeatType === 'weekday') {
        currentDate = snapDeadlineToPattern(t, currentDate);
      }
      if (selectedDate < currentDate) return false;

      let safety = 0;
      while (currentDate <= selectedDate && safety < 800) {
        if (currentDate === selectedDate) return true;
        const next = getNextDueDate(currentDate, t.repeatType, t.interval, t.monthDay, t.weekday, t.weekdays || t.repeatWeekdays);
        if (!next || next <= currentDate) break;
        currentDate = next;
        safety++;
      }
      return false;
    }).sort((a,b) => (a.deadline || '').localeCompare(b.deadline || ''));
  }, [tasks, selectedDate]);

  const [y, m] = viewMonth.split('-').map(Number);
  const monthName = new Date(y, m - 1, 1).toLocaleDateString('da-DK', { month: 'long', year: 'numeric' });

  function navigate(delta) {
    const [yy, mm] = viewMonth.split('-').map(Number);
    const d = new Date(yy, mm - 1 + delta, 1);
    setViewMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  }

  return (
    <div>
      <h1 style={{ margin: '0 0 16px', fontSize: 22, fontWeight: 700 }}>Kalender</h1>

      {/* Month navigator */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
      }}>
        <button onClick={() => navigate(-1)} style={navBtn(theme)} title="Forrige måned">
          <ChevronLeft size={18} />
        </button>
        <div style={{ fontSize: 16, fontWeight: 600, textTransform: 'capitalize' }}>{monthName}</div>
        <button onClick={() => navigate(1)} style={navBtn(theme)} title="Næste måned">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Calendar grid */}
      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.borderSubtle}`,
        borderRadius: 12,
        padding: 12,
        marginBottom: 16,
      }}>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 }}>
          {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map(d => (
            <div key={d} style={{
              textAlign: 'center',
              fontSize: 10,
              fontWeight: 600,
              color: theme.textDim,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              padding: '4px 0',
            }}>{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {monthData.map((cell, i) => {
            if (cell.empty) return <div key={i} />;
            const isToday = cell.date === today;
            const isSelected = cell.date === selectedDate;
            const hasTasks = cell.tasks.length > 0;

            return (
              <button
                key={cell.date}
                onClick={() => setSelectedDate(cell.date)}
                style={{
                  background: isSelected ? theme.coral : (isToday ? `${theme.coral}20` : 'transparent'),
                  color: isSelected ? 'white' : (isToday ? theme.coral : theme.text),
                  border: 'none',
                  borderRadius: 8,
                  padding: '6px 4px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 3,
                  minHeight: 44,
                  fontWeight: isToday || isSelected ? 700 : 500,
                  fontSize: 13,
                  position: 'relative',
                }}
              >
                <span>{cell.day}</span>
                {hasTasks && (
                  <div style={{ display: 'flex', gap: 2 }}>
                    {cell.hasSent && <Dot color={theme.coral} />}
                    {cell.hasShared && <Dot color={theme.teal} />}
                    {cell.hasPrivate && <Dot color={isSelected ? 'white' : theme.textMuted} />}
                    {cell.hasOutgoing && <Dot color={theme.gold} />}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex',
          gap: 12,
          marginTop: 12,
          paddingTop: 12,
          borderTop: `1px solid ${theme.borderSubtle}`,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
          {partner && <Legend theme={theme} color={theme.coral} label={`Opgaver fra ${partner.name}`} />}
          {partner && <Legend theme={theme} color={theme.teal} label="Fælles opgaver" />}
          <Legend theme={theme} color={theme.textMuted} label="Private opgaver" />
          {partner && <Legend theme={theme} color={theme.gold} label={`Sendt til ${partner.name}`} />}
        </div>
      </div>

      {/* Selected day tasks */}
      <div>
        <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600 }}>
          {(() => {
            const isToday = selectedDate === today;
            const d = new Date(selectedDate);
            return isToday ? 'I dag' : d.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' });
          })()}
        </h3>

        {selectedTasks.length === 0 ? (
          <div style={{
            padding: '20px',
            background: theme.bgCard,
            border: `1px solid ${theme.borderSubtle}`,
            borderRadius: 10,
            color: theme.textMuted,
            fontSize: 13,
            textAlign: 'center',
          }}>
            Ingen opgaver den dag.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {selectedTasks.map(t => (
              <TaskRow key={t.id} theme={theme} task={t}
                currentUserId={profile?.id} partner={partner}
                onComplete={onComplete} onDelete={onDelete} onEdit={onEdit}
                watchOnly={
                  // Hide all action buttons for tasks YOU sent to your partner
                  // (you shouldn't complete or edit those — they're theirs).
                  (t.list === 'sent' && t.createdBy === profile?.id && t.assignedTo !== profile?.id)
                }
                noComplete={selectedDate > today}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Dot({ color }) {
  return <div style={{ width: 4, height: 4, borderRadius: '50%', background: color }} />;
}

function Legend({ theme, color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: theme.textMuted }}>
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      <span>{label}</span>
    </div>
  );
}

function navBtn(theme) {
  return {
    background: theme.bgCard,
    border: `1px solid ${theme.borderSubtle}`,
    color: theme.text,
    padding: 8,
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}
